import React from 'react';
import { compose } from 'redux';
import { LocalizedMessage, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import _size from 'lodash/size';
import _toString from 'lodash/toString';
import PropTypes from 'prop-types';
// Core components
import Box from 'components/core/Box';
import Card from 'components/core/Card';
import Divider from 'components/core/Divider';
import List from 'components/core/List';
import Typography from 'components/core/Typography';
import AppIcon from 'components/icons/AppIcon';
import Tooltip from 'components/core/Tooltip';
import InformationItem from 'components/items/InformationItem';
import { InfoItem } from 'components/items/InformationItems';
import Callout from 'components/misc/Callout';
// Models
import mCarpark from 'models/cpm/carpark';
import mParking from 'models/cpm/parking';
import mBooking from 'models/cpm/booking';
// HOCs
import withPermissions from 'hocs/WithPermissions';
// Custom
import { cpmBookingDataShape, cpmDoorDataShape } from 'constants/PropTypesShapes';
import { dateGenerator } from 'utils/DateUtils';
import CarparkDoorItem from 'cpmCarparks/assets/main/CarparkDoorItem';

const BookingInformation = ({
  booking,
  timezone,
  doors,
}) => {
  const {
    customer,
    begin,
    end,
    spacesQuantity,
    comments,
    creationDate,
    keypadCodes,
    vehicles,
    doors: parkingDoors,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    restrictedByForbiddenEntry,
    restrictedByPermanentExit,
    restrictedByForbiddenExit,
  } = booking;

  // Get booking status
  const { icon: statusIcon, color: statusColor, value: statusValue } = mBooking.bookingStatus(booking);

  // Doors
  const doorsIds = _map(parkingDoors, 'id');

  /* Banners forbidden entry and/or exit */
  /* =================================== */

  const entryAndExitForbidden = booking.restrictedByForbiddenEntry && booking.restrictedByForbiddenExit;

  const BannerEntryAndExitForbidden = entryAndExitForbidden ? (
    <div className="col-12 pt-3">
      <Callout withInfoIcon severity="warning">
        <LocalizedMessage id="info.entryAndExitForbiddenAccessRight" />
      </Callout>
    </div>
  ) : null;

  const BannerForbiddenEntry = (!entryAndExitForbidden && booking.restrictedByForbiddenEntry) ? (
    <div className="col-12 pt-3">
      <Callout withInfoIcon severity="warning">
        <LocalizedMessage id="info.entryForbiddenAccessRight" />
      </Callout>
    </div>
  ) : null;

  const BannerForbiddenExit = (!entryAndExitForbidden && booking.restrictedByForbiddenExit) ? (
    <div className="col-12 pt-3">
      <Callout withInfoIcon severity="warning">
        <LocalizedMessage id="info.exitForbiddenAccessRight" />
      </Callout>
    </div>
  ) : null;

  /* Booking informations */
  /* ==================== */

  /* CreationDate */
  const CreationDate = creationDate ? (
    <React.Fragment>
      <InformationItem
        light
        icon="itemDateCreation"
        value={(
          <Tooltip content={(<LocalizedMessage id="field.creationDate" />)}>
            <span>
              <LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(creationDate, timezone)} />
            </span>
          </Tooltip>
        )}
      />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Booking status */
  const BookingStatus = (
    <React.Fragment>
      <InformationItem
        light
        icon={statusIcon}
        iconColor={statusColor}
        value={<LocalizedMessage id="info.bookingStatusDesc" values={{ status: statusValue }} />}
      />
      <Divider variant="middle" />
    </React.Fragment>
  );

  /* Booking reference */
  const BookingReference = (
    <React.Fragment>
      <InformationItem
        light
        icon="itemReference"
        value={(<Tooltip content={(<LocalizedMessage id="field.reference" />)}><span>{booking.reference}</span></Tooltip>)}
      />
      <Divider variant="middle" />
    </React.Fragment>
  );

  /* Customer name */
  const CustomerName = customer ? (
    <React.Fragment>
      <InformationItem
        light
        icon="itemCustomer"
        value={(<Tooltip content={(<LocalizedMessage id="field.customer" />)}><span>{`${customer.firstname || ''} ${customer.lastname || ''}`.trim()}</span></Tooltip>)}
      />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Customer email */
  const CustomerEmail = (customer && customer.email) ? (
    <React.Fragment>
      <InformationItem
        light
        icon="itemEmail"
        value={(<Tooltip content={(<LocalizedMessage id="field.email" />)}><span>{customer.email}</span></Tooltip>)}
      />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Customer phone */
  const CustomerPhone = (customer && customer.phone) ? (
    <React.Fragment>
      <InformationItem
        light
        icon="itemPhone"
        value={(<Tooltip content={(<LocalizedMessage id="field.phoneNumber" />)}><span>{customer.phone}</span></Tooltip>)}
      />
      <Divider variant="middle" />
    </React.Fragment>
  ) : null;

  /* Spaces quantity */
  const SpacesQuantity = (
    <InformationItem
      light
      icon="itemCarInfo"
      value={(<Tooltip content={(<LocalizedMessage id="field.spacesQuantity" />)}><span><LocalizedMessage id="info.spacesQuantity" values={{ quantity: _toString(spacesQuantity) }} /></span></Tooltip>)}
    />
  );

  /* Begin */
  const Begin = begin ? (
    <React.Fragment>
      <Divider variant="middle" />
      <InformationItem
        light
        icon="itemDateBegin"
        value={(<Tooltip content={(<LocalizedMessage id="field.beginDate" />)}><span><LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(begin, timezone)} /></span></Tooltip>)}
      />
    </React.Fragment>
  ) : null;

  /* End */
  const End = end ? (
    <React.Fragment>
      <Divider variant="middle" />
      <InformationItem
        light
        icon="itemDateEnd"
        value={(<Tooltip content={(<LocalizedMessage id="field.endDate" />)}><span><LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(end, timezone)} /></span></Tooltip>)}
      />
    </React.Fragment>
  ) : null;

  /* Comments */
  const Comments = comments ? (
    <React.Fragment>
      <Divider variant="middle" />
      <InformationItem
        light
        icon="itemComments"
        value={(<Tooltip content={(<LocalizedMessage id="field.comments" />)}><span>{comments}</span></Tooltip>)}
      />
    </React.Fragment>
  ) : null;

  const GeneralInformations = (
    <Card
      title={<Typography type="subtitle2"><LocalizedMessage id="title.generalInfo" /></Typography>}
      contentOptions={{ expandable: true, expanded: true }}
    >
      <List disablePadding>
        {CreationDate}
        {BookingStatus}
        {BookingReference}
        {CustomerName}
        {CustomerEmail}
        {CustomerPhone}
        {SpacesQuantity}
        {Begin}
        {End}
        {Comments}
      </List>
    </Card>
  );

  /* Carpark access ways */
  /* =================== */

  /* Keypad codes */
  const KeypadCodes = (
    <div>
      {_map(keypadCodes, (code, index) => (
        <React.Fragment key={index}>
          <InformationItem
            light
            icon="cAwTypeKCode"
            value={(<Tooltip content={(<LocalizedMessage id="field.keypadCodeValidation" />)}><span>{code}</span></Tooltip>)}
          />
          {index < _size(keypadCodes) - 1 && <Divider variant="middle" />}
        </React.Fragment>
      ))}
    </div>
  );

  /* Vehicles */
  const Vehicles = (
    <div>
      {_map(vehicles, ({ licensePlate }, index) => (
        <React.Fragment key={index}>
          <InformationItem
            light
            icon="cAwTypeLPlate"
            value={(<Tooltip content={(<LocalizedMessage id="field.licensePlate" />)}><span>{licensePlate}</span></Tooltip>)}
          />
          {index < _size(vehicles) - 1 && <Divider variant="middle" />}
        </React.Fragment>
      ))}
    </div>
  );

  const DividerKeypadVehicles = (KeypadCodes.length > 0 && Vehicles.length > 0) ? <div className="my-2" /> : null;

  const CarparkAccessWays = (
    <Card
      title={(<Typography type="subtitle2"><LocalizedMessage id="title.accessWaysCarpark" /></Typography>)}
      contentOptions={{
        expandable: true,
        expanded: true,
      }}
    >
      <List disablePadding>
        {KeypadCodes}
        {DividerKeypadVehicles}
        {Vehicles}
      </List>
    </Card>
  );

  /* Configuration */
  /* ============ */

  /* AntiPassback */
  const AntiPassback = (
    <React.Fragment>
      <div className="col-12">
        <Typography type="subtitle">
          <LocalizedMessage id="field.antipassback" />
        </Typography>
      </div>
      <div className="col-12 pt-1">
        {mParking.hasAntipassbackRestrictions(booking) ? (
          <List disablePadding>
            <InfoItem
              light
              checked={restrictedByAntipassbackEntrance}
              value={<LocalizedMessage id="field.restrictedEntrance" />}
            />
            <InfoItem
              light
              checked={restrictedByAntipassbackExit}
              value={<LocalizedMessage id="field.restrictedExit" />}
            />
          </List>
        ) : (
          <InformationItem
            light
            value={(
              <em>
                <LocalizedMessage id="info.notAppliedToAccessRight" />
              </em>
          )}
          />
        )}
      </div>
    </React.Fragment>
  );

  /* AccessAuthorized */
  const AccessAuthorized = (
    <React.Fragment>
      <div className="col-12 pt-3">
        <Typography type="subtitle">
          <LocalizedMessage id="field.doorsAuthorized" />
        </Typography>
      </div>
      <div className="col-12 pt-1">
        <List disablePadding>
          {_map(mCarpark.sortDoors(doors), (door) => (
            <CarparkDoorItem
              key={door.id}
              light
              door={door}
              rightIcon={
                <AppIcon defaultColor id={_includes(doorsIds, door.id) ? 'checkActive' : 'checkInactive'} />
              }
            />
          ))}
        </List>
      </div>
    </React.Fragment>
  );

  /* Options */
  const Options = (
    <React.Fragment>
      <div className="col-12 pt-3">
        <Typography type="subtitle">
          <LocalizedMessage id="field.options" />
        </Typography>
      </div>
      <div className="col-12 pt-1">
        <List disablePadding>
          <InfoItem
            light
            checked={restrictedByPermanentExit}
            value={<LocalizedMessage id="field.restrictedByPermanentExit" />}
          />
          <InfoItem
            light
            checked={restrictedByForbiddenEntry}
            value={<LocalizedMessage id="field.restrictedByForbiddenEntry" />}
          />
          <InfoItem
            light
            checked={restrictedByForbiddenExit}
            value={<LocalizedMessage id="field.restrictedByForbiddenExit" />}
          />
        </List>
      </div>
    </React.Fragment>
  );

  const Configuration = (
    <Card
      title={(<Typography type="subtitle2"><LocalizedMessage id="title.configuration" /></Typography>)}
      contentOptions={{ expandable: true, expanded: true }}
    >
      <div className="row">
        {AntiPassback}
        {AccessAuthorized}
        {Options}
      </div>
    </Card>
  );

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */

  return (
    <Box type="defaultBackground" className="p-3">
      <div className="row information-item-wrapper">
        {BannerEntryAndExitForbidden}
        {BannerForbiddenEntry}
        {BannerForbiddenExit}
        <div className="col-12 col-lg-6">
          <div className="mt-3">
            {GeneralInformations}
          </div>
          <div className="mt-3">
            {CarparkAccessWays}
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="mt-3">
            {Configuration}
          </div>
        </div>
      </div>
    </Box>
  );
};

/* ========================================================= */
/* PROPS                                                     */
/* ========================================================= */

BookingInformation.propTypes = {
  booking: cpmBookingDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  doors: PropTypes.arrayOf(cpmDoorDataShape).isRequired,
};

module.exports = compose(withPermissions)(BookingInformation);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/bookings/BookingInfo.js